/**
 * Memberships TanStack Query hooks (Phase 10D).
 *
 * All mutations surface errors via the existing react-hot-toast pattern.
 * All writes invalidate both the list and (where applicable) the detail cache.
 */

import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@core/api/client';
import {
  cancelMembership,
  createMembership,
  getMembership,
  listDLQEntries,
  listMembershipRenewals,
  listMemberships,
  pauseMembership,
  resolveDLQEntry,
  resumeMembership,
  skipNextMembership,
  updateMembership,
  type ListMembershipsParams,
  type MembershipCancelRequest,
  type MembershipCreateRequest,
  type MembershipDLQResolveRequest,
  type MembershipPauseRequest,
  type MembershipUpdateRequest,
} from './client';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const membershipKeys = {
  all: ['memberships'] as const,
  list: (params: ListMembershipsParams) =>
    ['memberships', 'list', params] as const,
  detail: (id: string) => ['memberships', 'detail', id] as const,
  renewals: (id: string) => ['memberships', 'renewals', id] as const,
  dlq: () => ['memberships', 'dlq'] as const,
};

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useMemberships(params: ListMembershipsParams = {}) {
  return useQuery({
    queryKey: membershipKeys.list(params),
    queryFn: () => listMemberships(params),
  });
}

export function useMembership(id: string) {
  return useQuery({
    queryKey: membershipKeys.detail(id),
    queryFn: () => getMembership(id),
    enabled: Boolean(id),
  });
}

export function useMembershipRenewals(id: string) {
  return useQuery({
    queryKey: membershipKeys.renewals(id),
    queryFn: () => listMembershipRenewals(id),
    enabled: Boolean(id),
  });
}

export function useDLQEntries(resolved?: boolean) {
  return useQuery({
    queryKey: membershipKeys.dlq(),
    queryFn: () => listDLQEntries(resolved),
  });
}

// ---------------------------------------------------------------------------
// Mutation helpers
// ---------------------------------------------------------------------------

function toastApiError(err: unknown, fallback: string): void {
  if (err instanceof ApiError) {
    toast.error(err.message || fallback);
  } else {
    toast.error(fallback);
  }
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MembershipCreateRequest) => createMembership(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: membershipKeys.all });
      toast.success('Membership created');
    },
    onError: (err) => toastApiError(err, 'Failed to create membership'),
  });
}

export function useUpdateMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MembershipUpdateRequest }) =>
      updateMembership(id, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: membershipKeys.all });
      void qc.invalidateQueries({ queryKey: membershipKeys.detail(id) });
      toast.success('Membership updated');
    },
    onError: (err) => toastApiError(err, 'Failed to update membership'),
  });
}

export function usePauseMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MembershipPauseRequest }) =>
      pauseMembership(id, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: membershipKeys.all });
      void qc.invalidateQueries({ queryKey: membershipKeys.detail(id) });
      toast.success('Membership paused');
    },
    onError: (err) => toastApiError(err, 'Failed to pause membership'),
  });
}

export function useResumeMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeMembership(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: membershipKeys.all });
      void qc.invalidateQueries({ queryKey: membershipKeys.detail(id) });
      toast.success('Membership resumed');
    },
    onError: (err) => toastApiError(err, 'Failed to resume membership'),
  });
}

export function useSkipNextMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => skipNextMembership(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: membershipKeys.all });
      void qc.invalidateQueries({ queryKey: membershipKeys.detail(id) });
      toast.success('Next renewal skipped');
    },
    onError: (err) => toastApiError(err, 'Failed to skip renewal'),
  });
}

export function useCancelMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MembershipCancelRequest }) =>
      cancelMembership(id, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: membershipKeys.all });
      void qc.invalidateQueries({ queryKey: membershipKeys.detail(id) });
      toast.success('Membership cancelled');
    },
    onError: (err) => toastApiError(err, 'Failed to cancel membership'),
  });
}

export function useResolveDLQEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dlqId,
      body,
    }: {
      dlqId: string;
      body: MembershipDLQResolveRequest;
    }) => resolveDLQEntry(dlqId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: membershipKeys.dlq() });
      // retry / skip / pause resolutions mutate the underlying membership;
      // invalidate its detail + renewals + the list so the UI doesn't
      // display stale state.
      const membershipId = data?.membership_id;
      if (membershipId) {
        void qc.invalidateQueries({
          queryKey: membershipKeys.detail(membershipId),
        });
        void qc.invalidateQueries({
          queryKey: membershipKeys.renewals(membershipId),
        });
        void qc.invalidateQueries({ queryKey: ['memberships', 'list'] });
      }
      // A retry that still failed leaves resolved=false; surface that
      // distinction so the operator knows the entry is still open.
      if (data?.resolved === false) {
        toast.error('Retry attempt failed — DLQ entry still open');
      } else {
        toast.success('DLQ entry resolved');
      }
    },
    onError: (err) => toastApiError(err, 'Failed to resolve DLQ entry'),
  });
}
